import { useEffect, useRef, useState } from 'react';

/**
 * Owner 2026-10-22 14:40: a new event / rental open in the side panel next to the calendar takes its dates from the
 * calendar, and the calendar marks the form's dates. `pick`: the days picked in the calendar last (a new object at
 * every pick, null before the first); `onDays`: the days of the form's dates, said whenever they change.
 */
export type CalendarLink = { pick: { days: string[] } | null; onDays: (days: string[]) => void };

/**
 * A new form's side of the link: `apply` puts picked days into the form's dates (nothing else of the form changes);
 * `days` are the form's dates as calendar days. Without a link (a phone, an edit) it does nothing.
 */
export function useCalendarLink(link: CalendarLink | undefined, days: readonly string[], apply: (days: string[]) => void): void {
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const onDays = useRef(link?.onDays);
  onDays.current = link?.onDays;
  const pick = link?.pick ?? null;
  useEffect(() => {
    if (pick) applyRef.current(pick.days);
  }, [pick]);
  const key = days.join(',');
  const linked = link !== undefined;
  useEffect(() => {
    if (linked) onDays.current?.(key ? key.split(',') : []);
  }, [key, linked]);
}

/**
 * The page's side: the last pick, kept only while dates are being picked (review M6) — closing the panel or making
 * the event drops it, so a form opened later never starts by applying an old pick.
 */
export function useDraftingPick(drafting: boolean): [CalendarLink['pick'], (pick: CalendarLink['pick']) => void] {
  const [pick, setPick] = useState<CalendarLink['pick']>(null);
  useEffect(() => {
    if (!drafting) setPick(null);
  }, [drafting]);
  return [drafting ? pick : null, setPick];
}
